// ─────────────────────────────────────────────────────────
// services/encryption.ts — AES-256-GCM encryption layer
//
// Phase 5: selective field-level encryption
//   OLD: entire doc → { _enc: string } blob
//   NEW: only SENSITIVE_FIELDS encrypted inline per collection
//
// Backward compat: decryptDoc detects old full-doc blobs at root
// level and decrypts them transparently (upgrade-on-read).
//
// API (unchanged signatures):
//   initEncryption(companyId)     — derive key
//   clearEncryptionKey()          — clear on logout
//   encryptDoc(obj, collection)   — selective encrypt
//   decryptDoc(raw)               — handles old + new format
//   encryptField(value)           — encrypt single value
//   decryptField(value)           — decrypt { _enc } value
// ─────────────────────────────────────────────────────────
import { getSensitiveFields } from './encryptionFields'

// ── Key state ─────────────────────────────────────────────
let _encPassphrase: string | null = null
let _encKey:        CryptoKey | null = null

// ── Passphrase from env ───────────────────────────────────
export async function fetchPassphrase(): Promise<string | null> {
  if (_encPassphrase) return _encPassphrase
  const val = import.meta.env.VITE_ENCRYPTION_PASSPHRASE as string | undefined
  if (val && val.length >= 16) {
    _encPassphrase = val
  } else {
    console.warn('CivilBillDesk: VITE_ENCRYPTION_PASSPHRASE not set — data saved unencrypted')
  }
  return _encPassphrase
}

// ── Key derivation (unchanged from Phase 1) ───────────────
async function deriveKey(passphrase: string, companyId: string): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(companyId), iterations: 200_000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

export async function initEncryption(companyId: string): Promise<void> {
  if (!companyId) { _encKey = null; return }
  try {
    const passphrase = await fetchPassphrase()
    if (!passphrase) { _encKey = null; return }
    _encKey = await deriveKey(passphrase, companyId)
  } catch {
    _encKey = null
  }
}

export function clearEncryptionKey(): void {
  _encKey        = null
  _encPassphrase = null
}

// ── Low-level: encrypt a single value ────────────────────
// Serialises value to JSON then AES-256-GCM encrypts it.
// Returns { _enc: base64string } or the original value on failure.
export async function encryptField(
  value: unknown
): Promise<{ _enc: string } | unknown> {
  if (!_encKey) return value
  if (value === null || value === undefined) return value
  try {
    const iv      = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(JSON.stringify(value))
    const cipher  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, _encKey, encoded)
    const combined = new Uint8Array(iv.byteLength + cipher.byteLength)
    combined.set(iv, 0)
    combined.set(new Uint8Array(cipher), iv.byteLength)
    return { _enc: btoa(String.fromCharCode(...combined)) }
  } catch {
    return value
  }
}

// ── Low-level: decrypt a single field value ───────────────
// Accepts { _enc: string } and returns the original value.
// Returns the input unchanged if it is not an encrypted blob.
export async function decryptField<T>(value: unknown): Promise<T> {
  if (!value || typeof value !== 'object') return value as T
  const maybeEnc = value as { _enc?: string }
  if (!maybeEnc._enc) return value as T
  if (!_encKey) return value as T
  try {
    const combined = Uint8Array.from(atob(maybeEnc._enc), c => c.charCodeAt(0))
    const iv       = combined.slice(0, 12)
    const cipher   = combined.slice(12)
    const decoded  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, _encKey, cipher)
    return JSON.parse(new TextDecoder().decode(decoded)) as T
  } catch {
    return value as T
  }
}

// ── encryptDoc — selective field encryption ───────────────
// Phase 5: only encrypts fields listed in SENSITIVE_FIELDS[collection].
// Non-sensitive fields remain plaintext (queryable, filterable).
// Falls back to full-doc encryption for unknown collections.
export async function encryptDoc<T extends object>(
  obj: T,
  collection = ''
): Promise<T | { _enc: string }> {
  if (!_encKey) return obj

  const sensitiveKeys = getSensitiveFields(collection)

  // No sensitive fields defined for this collection
  // → fall back to full-doc encryption (safe default)
  if (sensitiveKeys.size === 0) {
    return encryptFullDoc(obj)
  }

  try {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(obj)) {
      if (sensitiveKeys.has(key) && val !== undefined && val !== null) {
        result[key] = await encryptField(val)
      } else {
        result[key] = val
      }
    }
    return result as T
  } catch {
    // On any error, return plaintext — never lose data
    return obj
  }
}

// ── decryptDoc — handles BOTH old and new formats ─────────
// OLD format: { _enc: "base64..." } at root level
//             → decrypt entire blob, return parsed object
// NEW format: { status: "Paid", amount: { _enc: "..." }, ... }
//             → decrypt only the { _enc } fields inline
//
// This dual-mode makes Phase 5 fully backward-compatible:
// existing encrypted records in Supabase/IDB decode transparently.
export async function decryptDoc<T>(raw: T | { _enc: string }): Promise<T> {
  if (!raw || typeof raw !== 'object') return raw as T

  const maybeEnc = raw as { _enc?: string }

  // ── OLD format: root-level { _enc } blob ─────────────
  if (maybeEnc._enc) {
    if (!_encKey) return raw as T
    try {
      const combined = Uint8Array.from(atob(maybeEnc._enc), c => c.charCodeAt(0))
      const iv       = combined.slice(0, 12)
      const cipher   = combined.slice(12)
      const decoded  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, _encKey, cipher)
      return JSON.parse(new TextDecoder().decode(decoded)) as T
    } catch {
      return raw as T
    }
  }

  // ── NEW format: field-level { _enc } blobs ────────────
  // Walk every field; decrypt any that look like { _enc: string }
  const obj = raw as Record<string, unknown>
  const result: Record<string, unknown> = {}
  let hasEncryptedField = false

  for (const [key, val] of Object.entries(obj)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const maybeField = val as { _enc?: string }
      if (typeof maybeField._enc === 'string') {
        result[key] = await decryptField(val)
        hasEncryptedField = true
        continue
      }
    }
    result[key] = val
  }

  // If no encrypted fields found → return original (no allocation)
  return (hasEncryptedField ? result : raw) as T
}

// ── Internal: full-doc encryption (old strategy) ──────────
// Used as fallback for collections with no field map.
async function encryptFullDoc<T extends object>(
  obj: T
): Promise<T | { _enc: string }> {
  if (!_encKey) return obj
  try {
    const iv      = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(JSON.stringify(obj))
    const cipher  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, _encKey, encoded)
    const combined = new Uint8Array(iv.byteLength + cipher.byteLength)
    combined.set(iv, 0)
    combined.set(new Uint8Array(cipher), iv.byteLength)
    return { _enc: btoa(String.fromCharCode(...combined)) }
  } catch {
    return obj
  }
}
